import React, {useMemo} from "react";
import {Loader, LoaderCircle, MapPin} from "lucide-react";
import AppuntamentoCalendar from "@/components/vendi/form-permuta-steps/AppuntamentoCalendar";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/components/ui/collapsible";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {Separator} from "@/components/ui/separator";

interface Sede {
    _id: string;
    title: string;
    address: string;
    phone: string;
}

interface StepAppuntamentoProps {
    sedi: Sede[];
    isLoadingSedi: boolean;
    isErrorSedi: boolean;
    selectedSede: string;
    setSelectedSede: (sedeId: string) => void;
    selectedDate: Date | null;
    setSelectedDate: (date: Date) => void;
    selectedTimeSlot: string;
    setSelectedTimeSlot: (slot: string) => void;
    formDatiCliente?: any;
    eurotaxValutazione?: EurotaxValutazione | null;
    isValutazioneLoading?: boolean;
    isClienteProfileComplete?: boolean;
    isManualValutazioneOverride?: boolean;
    onEnableValutazioneOverride?: () => void;
    onDisableValutazioneOverride?: () => void;
    showValutazioneSection?: boolean;
}

const isDevelopment = process.env.NODE_ENV !== "production";

const toFiniteNumber = (value: unknown): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
        const normalized = value.trim().replace(",", ".");
        if (!normalized) return null;
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const firstFiniteNumber = (...values: Array<unknown>): number | null => {
    for (const value of values) {
        const parsed = toFiniteNumber(value);
        if (parsed !== null) return parsed;
    }
    return null;
};

const formatCurrency = (value: number | null | undefined) => {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
    }).format(value);
};

const roundDownToHundreds = (value: number | null | undefined) => {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    return Math.floor(value / 100) * 100;
};

const formatCurrencyWithoutDecimals = (value: number | null | undefined) => {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

const formatPercentage = (value: number | null | undefined) => {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    const normalized = Math.abs(value) <= 1 ? value * 100 : value;
    return `${normalized.toFixed(2)}%`;
};

const getPrimaryDisplayValue = (valutazione?: EurotaxValutazione | null) => {
    if (!valutazione) return null;
    if (valutazione.quotazioneProposta === 0) return null;

    if (typeof valutazione.quotazioneProposta === "number") {
        return formatCurrencyWithoutDecimals(roundDownToHundreds(valutazione.quotazioneProposta));
    }

    const legacySingleValue = firstFiniteNumber(
        valutazione.valoreRiferimento,
        valutazione.valore,
        valutazione.quotazione,
        valutazione.prezzo,
        valutazione.max,
        valutazione.min,
    );

    return legacySingleValue !== null ? formatCurrency(legacySingleValue) : null;
};

const renderChipList = (values?: string[]) => {
    if (!values?.length) return null;

    return (
        <div className="flex flex-wrap gap-2">
            {values.map((value) => (
                <Badge key={value} variant="outline" className="rounded-md px-2 py-1 text-[11px] font-medium">
                    {value}
                </Badge>
            ))}
        </div>
    );
};

const StepAppuntamento: React.FC<StepAppuntamentoProps> = ({
    sedi,
    isLoadingSedi,
    isErrorSedi,
    selectedSede,
    setSelectedSede,
    selectedDate,
    setSelectedDate,
    selectedTimeSlot,
    setSelectedTimeSlot,
    formDatiCliente,
    eurotaxValutazione,
    isValutazioneLoading = false,
    isClienteProfileComplete = false,
    isManualValutazioneOverride = false,
    onEnableValutazioneOverride,
    onDisableValutazioneOverride,
    showValutazioneSection = false,
}) => {
    const getEasterSunday = (year: number) => {
        const f = Math.floor;
        const a = year % 19;
        const b = f(year / 100);
        const c = year % 100;
        const d = f(b / 4);
        const e = b % 4;
        const g = f((8 * b + 13) / 25);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = f(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = f((a + 11 * h + 19 * l) / 433);
        const month = f((h + l - 7 * m + 90) / 25) - 1;
        const day = (h + l - 7 * m + 33 * month + 19) % 32;
        return new Date(year, month, day);
    };

    const isItalianHoliday = (date: Date) => {
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();

        const fixedHolidays = new Set([
            "1-1",
            "6-1",
            "25-4",
            "1-5",
            "2-6",
            "15-8",
            "1-11",
            "8-12",
            "25-12",
            "26-12",
        ]);

        if (fixedHolidays.has(`${day}-${month}`)) return true;

        const easter = getEasterSunday(year);
        const easterMonday = new Date(easter);
        easterMonday.setDate(easter.getDate() + 1);

        return (
            date.toDateString() === easter.toDateString() ||
            date.toDateString() === easterMonday.toDateString()
        );
    };

    const getSlotsForDate = (date: Date | null) => {
        if (!date) return [];
        if (isItalianHoliday(date)) return [];

        const weekday = date.getDay();
        if (weekday >= 1 && weekday <= 5) {
            return ["09:00", "10:00", "11:00", "12:00", "15:00", "16:00", "17:00", "18:00"];
        }
        if (weekday === 6) {
            return ["09:00", "10:00", "11:00", "12:00"];
        }
        return [];
    };

    const isDateSelectable = (date: Date) => getSlotsForDate(date).length > 0;
    const availableSlots = useMemo(() => getSlotsForDate(selectedDate), [selectedDate]);

    const primaryDisplayValue = getPrimaryDisplayValue(eurotaxValutazione);
    const coefficienteDisplay = formatPercentage(eurotaxValutazione?.coefficienteValutazione);
    const margineDisplay = formatCurrency(eurotaxValutazione?.margineAggiuntivoDesiderato);
    const margineDbDisplay = formatPercentage(eurotaxValutazione?.margineDbDiagnostico);
    const valoreRiferimentoDisplay = formatCurrency(eurotaxValutazione?.valoreRiferimento);
    const streetPriceFallbackDisplay = formatCurrency(eurotaxValutazione?.streetPriceMinimoFallback);
    const showDebugPanel = isDevelopment && !!eurotaxValutazione;
    const hasZeroProposta = eurotaxValutazione?.quotazioneProposta === 0;
    const shouldShowManualValutazioneInput = isClienteProfileComplete && !isValutazioneLoading && !primaryDisplayValue;
    const shouldShowValutazioneSection = showValutazioneSection && !!formDatiCliente;

    const valutazioneSection = shouldShowValutazioneSection ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4 space-y-4">
            <div className="space-y-1">
                <p className="text-sm font-medium text-slate-600">Valore di vendita della tua auto:</p>
                {!isClienteProfileComplete ? (
                    <>
                        <p className="text-lg font-semibold text-slate-900">Completa i dati cliente</p>
                        <p className="text-sm text-slate-500">
                            Inserisci nome, cognome, telefono, provincia e targa per avviare il calcolo automatico.
                        </p>
                    </>
                ) : isValutazioneLoading ? (
                    <>
                        <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-200 text-slate-600">
                                <LoaderCircle className="h-6 w-6 animate-spin"/>
                            </div>
                            <div className="space-y-1">
                                <p className="text-lg font-semibold text-slate-950">Sto calcolando il valore della tua auto...</p>
                                <p className="text-sm text-slate-500">
                                    Analizzo veicolo, coefficienti e dati supplementari disponibili prima di mostrarti l&apos;importo.
                                </p>
                            </div>
                        </div>
                        <p className="text-sm text-slate-500">Attendi qualche secondo, il risultato arriva in automatico.</p>
                    </>
                ) : (
                    <>
                        <p className="text-3xl font-semibold tracking-tight text-slate-950">
                            {primaryDisplayValue ?? "Valutazione automatica non disponibile"}
                        </p>
                        <p className="text-sm text-slate-500">
                            {hasZeroProposta
                                ? "Non e' stato possibile determinare automaticamente il valore della tua auto sulla base dei dati forniti. Ti invitiamo quindi a indicare l'importo desiderato."
                                : primaryDisplayValue
                                    ? "Questo importo e' una stima di vendita iniziale basata sulle informazioni che hai inserito."
                                    : "Non ho ricevuto una proposta valida. Puoi indicare manualmente l'importo desiderato."}
                        </p>
                    </>
                )}
            </div>

            {shouldShowManualValutazioneInput && formDatiCliente ? (
                <Form {...formDatiCliente}>
                    <FormField control={formDatiCliente.control} name="valutazione"
                               render={({field, fieldState}) => (
                                   <FormItem>
                                       <FormLabel>Importo desiderato</FormLabel>
                                       <FormControl>
                                           <Input {...field} placeholder="Inserisci l'importo desiderato"/>
                                       </FormControl>
                                       <FormMessage>{fieldState.error?.message}</FormMessage>
                                   </FormItem>
                               )}
                    />
                </Form>
            ) : null}

            {showDebugPanel && isClienteProfileComplete && !isValutazioneLoading ? (
                <Collapsible>
                    <CollapsibleTrigger asChild>
                        <Button type="button" variant="ghost" className="h-auto px-0 text-xs text-slate-500 hover:bg-transparent hover:text-slate-900">
                            Mostra diagnostica Eurotax
                        </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-4 pt-2">
                        {(valoreRiferimentoDisplay || coefficienteDisplay || eurotaxValutazione?.fonteCoefficiente || margineDisplay) ? (
                            <>
                                <Separator/>
                                <div className="grid gap-3 md:grid-cols-2">
                                    {valoreRiferimentoDisplay ? (
                                        <div className="space-y-1">
                                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Base calcolo</p>
                                            <p className="text-sm font-semibold text-slate-900">{valoreRiferimentoDisplay}</p>
                                        </div>
                                    ) : null}
                                    {coefficienteDisplay && eurotaxValutazione?.fonteCoefficiente === "db" ? (
                                        <div className="space-y-1">
                                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Coefficiente DB</p>
                                            <p className="text-sm font-semibold text-slate-900">{coefficienteDisplay}</p>
                                        </div>
                                    ) : null}
                                    {eurotaxValutazione?.fonteCoefficiente ? (
                                        <div className="space-y-1">
                                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Origine coefficiente</p>
                                            <p className="text-sm font-semibold text-slate-900">{eurotaxValutazione.fonteCoefficiente}</p>
                                        </div>
                                    ) : null}
                                    {margineDisplay ? (
                                        <div className="space-y-1">
                                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Margine aggiuntivo</p>
                                            <p className="text-sm font-semibold text-slate-900">{margineDisplay}</p>
                                        </div>
                                    ) : null}
                                </div>
                            </>
                        ) : null}

                        <div className="grid gap-3 md:grid-cols-2">
                            {eurotaxValutazione?.codiceCostruttore ? (
                                <div className="space-y-1">
                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Codice costruttore Eurotax</p>
                                    <p className="text-sm text-slate-900">{eurotaxValutazione.codiceCostruttore}</p>
                                </div>
                            ) : null}
                            {margineDbDisplay ? (
                                <div className="space-y-1">
                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Margine DB diagnostico (%)</p>
                                    <p className="text-sm text-slate-900">{margineDbDisplay}</p>
                                </div>
                            ) : null}
                            {streetPriceFallbackDisplay ? (
                                <div className="space-y-1">
                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Fallback Street Price Min</p>
                                    <p className="text-sm text-slate-900">{streetPriceFallbackDisplay}</p>
                                </div>
                            ) : null}
                            {eurotaxValutazione?.codiciCostruttoreAlternativi?.length ? (
                                <div className="space-y-2">
                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Codici costruttore alternativi</p>
                                    {renderChipList(eurotaxValutazione.codiciCostruttoreAlternativi)}
                                </div>
                            ) : null}
                            {eurotaxValutazione?.codiciCostruttoreRicerca?.length ? (
                                <div className="space-y-2 md:col-span-2">
                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Chiavi ricerca DB</p>
                                    {renderChipList(eurotaxValutazione.codiciCostruttoreRicerca)}
                                </div>
                            ) : null}
                        </div>

                        {eurotaxValutazione?.rigaEurotaxDb ? (
                            <details className="rounded-md border border-slate-200 bg-white p-3">
                                <summary className="cursor-pointer text-xs font-medium uppercase tracking-wide text-slate-600">
                                    Riga raw Eurotax DB
                                </summary>
                                <pre className="mt-3 overflow-x-auto rounded-md bg-slate-950 p-3 text-xs text-slate-100">
                                    {JSON.stringify(eurotaxValutazione.rigaEurotaxDb, null, 2)}
                                </pre>
                            </details>
                        ) : null}
                    </CollapsibleContent>
                </Collapsible>
            ) : null}
        </div>
    ) : null;

    return (
        <div className="space-y-6 md:space-y-8">
            <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-6 lg:gap-8 items-start">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                    <h3 className="text-lg font-semibold text-gray-900">Scegli la sede</h3>
                    <p className="text-sm text-gray-500 mt-1">Seleziona la sede più comoda per te</p>
                    {isLoadingSedi ? (
                        <div className="flex items-center gap-2 mt-4 text-gray-400">
                            <Loader className="h-4 w-4 animate-spin"/>
                            <span className="text-sm">Caricamento sedi...</span>
                        </div>
                    ) : isErrorSedi ? (
                        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                            Errore nel caricamento delle sedi. Riprova tra qualche secondo.
                        </div>
                    ) : sedi.length === 0 ? (
                        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">
                            Nessuna sede disponibile al momento.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                            {sedi.map((sede) => (
                                <button
                                    key={sede._id}
                                    type="button"
                                    onClick={() => setSelectedSede(sede._id)}
                                    className={`text-left rounded-xl border p-4 transition-all cursor-pointer ${
                                        selectedSede === sede._id
                                            ? "border-primary bg-primary/10 ring-1 ring-primary"
                                            : "border-slate-200 hover:border-slate-300 bg-white"
                                    }`}
                                >
                                    <p className="font-semibold text-gray-900 text-sm">{sede.title}</p>
                                    {sede.address ? (
                                        <p className="text-xs text-gray-500 mt-1 flex items-start gap-1">
                                            <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5"/>
                                            {sede.address}
                                        </p>
                                    ) : null}
                                    {sede.phone ? (
                                        <p className="text-xs text-gray-400 mt-1">{sede.phone}</p>
                                    ) : null}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
                    <h3 className="text-lg font-semibold text-gray-900">Scegli la data</h3>
                    {selectedSede ? (
                        <>
                            <p className="text-sm text-gray-500 mt-1">Seleziona una data entro i prossimi 30 giorni (festivi esclusi)</p>
                            <div className="mt-4 flex justify-center">
                                <AppuntamentoCalendar
                                    selectedDate={selectedDate}
                                    onSelect={setSelectedDate}
                                    isDateSelectable={isDateSelectable}
                                />
                            </div>
                            {selectedDate ? (
                                <div className="mt-5">
                                    <h4 className="text-sm font-semibold text-gray-900">Scegli l'orario</h4>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Lun-Ven: 09:00-13:00, 15:00-19:00 | Sab: 09:00-13:00
                                    </p>
                                    {availableSlots.length > 0 ? (
                                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {availableSlots.map((slot) => (
                                                <button
                                                    key={slot}
                                                    type="button"
                                                    onClick={() => setSelectedTimeSlot(slot)}
                                                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                                                        selectedTimeSlot === slot
                                                            ? "border-primary bg-primary text-white"
                                                            : "border-slate-200 bg-white text-gray-700 hover:border-slate-300"
                                                    }`}
                                                >
                                                    {slot}
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-500">
                                            Nessuno slot disponibile per questa data.
                                        </div>
                                    )}
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
                            Seleziona prima una sede per aprire il calendario.
                        </div>
                    )}
                </div>
            </div>

            {(selectedSede || selectedDate) ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-1 max-w-3xl mx-auto w-full">
                    {selectedSede ? (
                        <p className="text-sm text-gray-700">
                            <span className="font-medium">Sede:</span> {sedi.find((s) => s._id === selectedSede)?.title}
                        </p>
                    ) : null}
                    {selectedDate ? (
                        <p className="text-sm text-gray-700">
                            <span className="font-medium">Data:</span>{" "}
                            {selectedDate.toLocaleDateString("it-IT", {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                                year: "numeric"
                            })}
                        </p>
                    ) : null}
                    {selectedTimeSlot ? (
                        <p className="text-sm text-gray-700">
                            <span className="font-medium">Orario:</span> {selectedTimeSlot}
                        </p>
                    ) : null}
                </div>
            ) : null}

            {valutazioneSection}
        </div>
    );
};

export default StepAppuntamento;
